import React, { useState, useCallback, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton } from 'components/modals';
import { SyutSelect, type SelectOption } from 'components/ui';
import { EnterpriseScreenShell, actionButtonFactory, useCan } from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { bankReconciliationService, coaLedgerService } from 'api/services';
import type { BankReconciliationStatementDto, BrsItemDto } from 'api/types';
import ReportHeader from 'components/reports/ReportHeader';
import { ReconciliationStartModal } from '../../modals/modules/banking/ReconciliationStartModal';
import { ChargeRulesModal } from '../../modals/modules/banking/ChargeRulesModal';

// ============================================================================
// BANK RECONCILIATION (ADR-0055 §7-8) — the Bank Reconciliation Statement of a
// bank account as at any date: balance per books, the items in transit either
// way, the bank balance they imply, and the statement's own balance beside it.
// The Reconciliation Start (set once per account) and the charge rules open from
// here as dialogs. Matching itself happens on Bank Statements.
// ============================================================================

const today = () => new Date().toISOString().slice(0, 10);
const money = (n?: number | null) => (n == null ? '—' : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleDateString() : '');
const inp: React.CSSProperties = { fontSize: '0.8125rem', padding: '0.375rem 0.625rem' };
const muted: React.CSSProperties = { color: 'var(--text-muted)' };
const th: React.CSSProperties = { textAlign: 'left', padding: '0.4rem 0.6rem', fontWeight: 600 };
const td: React.CSSProperties = { padding: '0.35rem 0.6rem', verticalAlign: 'top' };
const esc = (s?: string | null) => (s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));

interface Section {
  key: 'dnc' | 'pnp' | 'bc' | 'bd';
  sign: 'Less' | 'Add';
  title: string;
  hint: string;
  items: (d: BankReconciliationStatementDto) => BrsItemDto[];
  total: (d: BankReconciliationStatementDto) => number;
}

const SECTIONS: Section[] = [
  { key: 'dnc', sign: 'Less', title: 'Deposits not yet credited by the bank', hint: 'In the books, not yet on a statement',
    items: d => d.depositsNotCredited, total: d => d.depositsNotCreditedTotal },
  { key: 'pnp', sign: 'Add', title: 'Cheques / payments not yet presented', hint: 'In the books, not yet through the bank',
    items: d => d.paymentsNotPresented, total: d => d.paymentsNotPresentedTotal },
  { key: 'bc', sign: 'Add', title: 'Credits in the bank not in the books', hint: 'Interest, direct credits — post them from Bank Statements',
    items: d => d.bankCreditsNotInBooks, total: d => d.bankCreditsNotInBooksTotal },
  { key: 'bd', sign: 'Less', title: 'Debits in the bank not in the books', hint: 'Charges, direct debits — post them from Bank Statements',
    items: d => d.bankDebitsNotInBooks, total: d => d.bankDebitsNotInBooksTotal },
];

const KIND_LABEL: Record<BrsItemDto['kind'], string> = { BOOK: 'Voucher', OPENING: 'Opening item', LINE: 'Statement', PARTIAL: 'Part-cleared' };

interface BankReconciliationScreenProps {
  moduleIcon?: string;
  tabId?: string;
}

const BankReconciliationScreen: React.FC<BankReconciliationScreenProps> = ({ moduleIcon, tabId }) => {
  const can = useCan('bank-reconciliation');
  const [filterOpen, setFilterOpen] = useState(true);
  const [bankId, setBankId] = useState('');
  const [asAt, setAsAt] = useState(today());
  const [data, setData] = useState<BankReconciliationStatementDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [chargeRulesOpen, setChargeRulesOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const labelCache = useRef<Map<string, string>>(new Map());
  const bankLabel = bankId ? (labelCache.current.get(bankId) || data?.bankLedgerName || '') : '';

  const loadBanks = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await coaLedgerService.getAll({ pageSize: 50, searchTerm: input || undefined, ledgerNature: 'BANK' });
      res.data.forEach(l => labelCache.current.set(l.id, `${l.ledgerCode} - ${l.ledgerName}`));
      return res.data.map(l => ({ value: l.id, label: `${l.ledgerCode} - ${l.ledgerName}` }));
    } catch {
      return [];
    }
  }, []);

  const load = useCallback(async (bank = bankId, date = asAt) => {
    if (!bank) { notificationService.toast.warning('Choose the bank account.'); return; }
    setLoading(true);
    try {
      setData(await bankReconciliationService.getStatement(bank, date));
      setFilterOpen(false);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not build the reconciliation statement.');
    } finally {
      setLoading(false);
    }
  }, [bankId, asAt]);

  const handleExport = useCallback(async () => {
    if (!data) return;
    setExporting(true);
    try {
      await bankReconciliationService.exportStatement(data.bankLedgerId, data.asAtDate.slice(0, 10));
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not export the reconciliation statement.');
    } finally {
      setExporting(false);
    }
  }, [data]);

  // A clean printable page (browser print → PDF), without the app chrome.
  const handlePrint = useCallback(() => {
    if (!data) return;
    const row = (label: string, value: string, bold = false) =>
      `<tr${bold ? ' class="b"' : ''}><td colspan="4">${esc(label)}</td><td class="n">${esc(value)}</td></tr>`;
    const sections = SECTIONS.map(s => {
      const items = s.items(data);
      const lines = items.length === 0
        ? '<tr><td></td><td colspan="3" class="m">None</td><td></td></tr>'
        : items.map(i => `<tr><td>${esc(fmtDate(i.date))}</td><td>${esc(i.reference)}</td><td>${esc([i.partyName, i.description].filter(Boolean).join(' · '))}</td><td class="m">${esc(i.note)}</td><td class="n">${esc(money(i.amount))}</td></tr>`).join('');
      return `<tr class="h"><td colspan="5">${s.sign}: ${esc(s.title)}</td></tr>${lines}${row(`Total ${s.title.toLowerCase()}`, money(s.total(data)), true)}`;
    }).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Bank Reconciliation Statement</title><style>
      body{font-family:Segoe UI,Arial,sans-serif;font-size:11px;color:#111;margin:18px}
      h1{font-size:16px;margin:0 0 4px} .meta{color:#555;margin-bottom:10px}
      table{width:100%;border-collapse:collapse} td{padding:3px 6px;border-bottom:1px solid #eee;vertical-align:top}
      .n{text-align:right;white-space:nowrap;font-variant-numeric:tabular-nums} .m{color:#666} .b td{font-weight:700}
      .h td{background:#eef2f7;font-weight:700;padding-top:6px} .k td{font-weight:700;font-size:12px;border-bottom:2px solid #999}
      .w{margin-top:10px;color:#92400e} @page{size:A4;margin:14mm}
    </style></head><body>
      <h1>Bank Reconciliation Statement</h1>
      <div class="meta">${esc(data.bankLedgerName)} · as at ${esc(fmtDate(data.asAtDate))}${data.currencyCode ? ` · ${esc(data.currencyCode)}` : ''}${data.startDate ? ` · reconciled from ${esc(fmtDate(data.startDate))}` : ''}</div>
      <table>
        <tr class="k"><td colspan="4">Balance as per books</td><td class="n">${esc(money(data.bookBalance))}</td></tr>
        ${sections}
        <tr class="k"><td colspan="4">Balance as per bank (computed)</td><td class="n">${esc(money(data.computedBankBalance))}</td></tr>
        ${row(data.statementBalanceSource === 'START_PLUS_LINES' ? 'Balance as per bank statement (start + imported lines)' : 'Balance as per bank statement', money(data.statementBalance))}
        ${row('Difference', money(data.difference), true)}
      </table>
      ${data.warnings.map(w => `<div class="w">Note: ${esc(w)}</div>`).join('')}
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
    const w = window.open('', '_blank');
    if (!w) { notificationService.toast.warning('Allow pop-ups to print the statement.'); return; }
    w.document.write(html);
    w.document.close();
  }, [data]);

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.custom('start', <IconSystem.calendar size={18} />, 'Reconciliation Start', () => setStartOpen(true),
        { tooltip: 'Set once per bank: the start date, the bank balance on it and the items then outstanding' }),
      disabled: !bankId },
    { ...actionButtonFactory.custom('charge-rules', <IconSystem.settings size={18} />, 'Charge Rules', () => setChargeRulesOpen(true),
        { tooltip: 'Expected bank / gateway charges per payment mode' }),
      disabled: !bankId },
    actionButtonFactory.refresh(() => load()),
    ...(can('EXPORT') ? [{ ...actionButtonFactory.export_(handleExport), disabled: !data || exporting }] : []),
    { ...actionButtonFactory.print(handlePrint, 'Print or save as PDF'), disabled: !data },
  ];

  const diffOk = data?.difference != null && Math.abs(data.difference) < 0.005;
  const summaryRow = (label: React.ReactNode, value: React.ReactNode, opts: { strong?: boolean; color?: string; top?: boolean } = {}) => (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: '1rem', padding: '0.3rem 0',
      fontWeight: opts.strong ? 700 : 400, color: opts.color,
      borderTop: opts.top ? '1px solid var(--border-color)' : undefined,
    }}>
      <span>{label}</span><span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );

  return (
    <EnterpriseScreenShell
      title="Bank Reconciliation"
      description="Bank Reconciliation Statement of a bank account as at any date"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.link size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Statement</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label="Bank account" isAsync loadOptions={loadBanks}
                  defaultOptions cacheOptions={false}
                  seedOptions={bankId ? [{ value: bankId, label: labelCache.current.get(bankId) || '' }] : undefined}
                  menuPortalTarget={document.body} value={bankId}
                  onChange={v => { setBankId((v as string) || ''); setData(null); }}
                  placeholder="Pick a bank account" isClearable size="sm" />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>As at</label>
                <input type="date" className="form-input" value={asAt} onChange={e => setAsAt(e.target.value)} style={inp} />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => { setAsAt(today()); }}>Today</button>
              <button className="btn btn-primary" onClick={() => load()} disabled={loading}>{loading ? 'Loading…' : 'Show'}</button>
            </div>
          </div>
        )
      }
    >
      <ReportHeader filters={[
        { label: 'Bank', value: bankLabel || '—' },
        { label: 'As At', value: fmtDate(asAt) },
        { label: 'Reconciled From', value: data ? (data.startDate ? fmtDate(data.startDate) : 'No start set') : '—' },
      ]} />

      {!data && (
        <div style={{ ...muted, padding: '2rem', textAlign: 'center', border: '1px solid var(--border-color)', borderTop: 'none', borderRadius: '0 0 0.5rem 0.5rem' }}>
          Pick a bank account and a date, then click Show.
        </div>
      )}

      {data && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', border: '1px solid var(--border-color)', borderTop: 'none', borderRadius: '0 0 0.5rem 0.5rem', padding: '1rem' }}>
          {!data.startDate && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0.8rem', marginBottom: '0.75rem',
              border: '1px solid #f59e0b', background: 'rgba(245,158,11,0.08)', borderRadius: '0.5rem', fontSize: '0.8125rem' }}>
              <IconSystem.warning size={16} />
              <span style={{ flex: 1 }}>
                No Reconciliation Start on this account, so every bank entry since the books began is treated as outstanding.
                Set the date reconciliation begins from, the bank balance on it and the items then outstanding.
              </span>
              {can('UPDATE') && <button className="btn btn-primary" style={{ fontSize: '0.78rem', padding: '0.3rem 0.7rem' }} onClick={() => setStartOpen(true)}>Set Reconciliation Start</button>}
            </div>
          )}
          {data.warnings.filter(w => !w.startsWith('No Reconciliation Start')).map(w => (
            <div key={w} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', padding: '0.45rem 0.7rem', marginBottom: '0.5rem',
              border: '1px solid var(--border-color)', borderLeft: '3px solid #f59e0b', borderRadius: '0.375rem', fontSize: '0.78rem' }}>
              <IconSystem.info size={14} /><span>{w}</span>
            </div>
          ))}

          {/* Summary */}
          <div style={{ maxWidth: 620, marginLeft: 'auto', marginBottom: '1rem', border: '1px solid var(--border-color)', borderRadius: '0.5rem', padding: '0.6rem 0.9rem', fontSize: '0.85rem' }}>
            {summaryRow('Balance as per books', money(data.bookBalance), { strong: true })}
            {SECTIONS.map(s => summaryRow(
              <span>{s.sign}: {s.title} <span style={muted}>({s.items(data).length})</span></span>,
              money(s.total(data)),
            ))}
            {summaryRow('Balance as per bank (computed)', money(data.computedBankBalance), { strong: true, top: true })}
            {summaryRow(
              <span>Balance as per bank statement{data.statementBalanceSource === 'START_PLUS_LINES' && <span style={muted}> (start + imported lines)</span>}</span>,
              money(data.statementBalance),
            )}
            {summaryRow('Difference', data.difference == null ? '—' : money(data.difference),
              { strong: true, top: true, color: data.difference == null ? undefined : diffOk ? '#059669' : '#dc2626' })}
            {data.unmatchedLineCount > 0 && (
              <div style={{ ...muted, fontSize: '0.75rem', marginTop: '0.3rem' }}>
                {data.unmatchedLineCount} statement line(s) up to this date are still unmatched — match or post them on Bank Statements.
              </div>
            )}
          </div>

          {/* Items */}
          {SECTIONS.map(s => {
            const items = s.items(data);
            const isCollapsed = collapsed[s.key] ?? items.length === 0;
            return (
              <div key={s.key} style={{ marginBottom: '0.75rem', border: '1px solid var(--border-color)', borderRadius: '0.5rem', overflow: 'hidden' }}>
                <button type="button" onClick={() => setCollapsed(c => ({ ...c, [s.key]: !isCollapsed }))}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', border: 'none',
                    background: 'var(--grid-header-bg, #dbeafe)', cursor: 'pointer', fontSize: '0.8125rem', textAlign: 'left', color: 'var(--text-primary)' }}>
                  {isCollapsed ? <IconSystem.chevronRight size={14} /> : <IconSystem.chevronDown size={14} />}
                  <strong>{s.sign}: {s.title}</strong>
                  <span style={muted}>· {items.length} item(s) · {s.hint}</span>
                  <span style={{ marginLeft: 'auto', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{money(s.total(data))}</span>
                </button>
                {!isCollapsed && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <th style={{ ...th, width: 100 }}>Date</th>
                        <th style={{ ...th, width: 160 }}>Reference</th>
                        <th style={th}>Particulars</th>
                        <th style={{ ...th, width: 110 }}>Source</th>
                        <th style={{ ...th, width: 170 }}>Status</th>
                        <th style={{ ...th, width: 130, textAlign: 'right' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 && <tr><td colSpan={6} style={{ ...td, ...muted, textAlign: 'center' }}>None</td></tr>}
                      {items.map((i, n) => (
                        <tr key={`${i.transactionId ?? i.lineId ?? i.matchId ?? 'x'}-${n}`} style={{ borderTop: '1px solid var(--border-color)' }}>
                          <td style={td}>{fmtDate(i.date)}</td>
                          <td style={td}>{i.reference || '—'}{i.statementRef && <div style={{ ...muted, fontSize: '0.72rem' }}>{i.statementRef}</div>}</td>
                          <td style={td}>{[i.partyName, i.description].filter(Boolean).join(' · ') || '—'}</td>
                          <td style={{ ...td, ...muted }}>{KIND_LABEL[i.kind]}</td>
                          <td style={{ ...td, ...muted }}>{i.note}</td>
                          <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{money(i.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      )}

      {bankId && (
        <ReconciliationStartModal isOpen={startOpen} bankLedgerId={bankId} bankLedgerName={bankLabel || data?.bankLedgerName}
          canUpdate={can('UPDATE')} canDelete={can('DELETE')}
          onClose={() => setStartOpen(false)}
          onSaved={() => { if (data) load(); }} />
      )}
      {bankId && (
        <ChargeRulesModal isOpen={chargeRulesOpen} bankLedgerId={bankId}
          bankLedgerName={bankLabel || data?.bankLedgerName || undefined} canUpdate={can('UPDATE')}
          onClose={() => setChargeRulesOpen(false)} />
      )}
    </EnterpriseScreenShell>
  );
};

export default BankReconciliationScreen;
